import type { ChannelClient } from '@hibro/platform-core';
import type {
  AutoLaunchBridge,
  BundleInstallBridge,
  DevtoolsBridge,
  SystemNotificationSettingsBridge,
} from '@hibro/platform-renderer';
import { useEffect, useMemo, useState } from 'react';

import { createBundleBridge, type PickLocalBundle } from './bundle-bridge';

/**
 * Bridges the old platform settings panel needs, built in the shell so
 * platform-renderer never touches preload globals. Shared by the workbench
 * modal and the preferences window's developer-options page.
 */
export function useSettingsCapabilityBridges(
  client: ChannelClient,
  pickLocal?: PickLocalBundle,
): {
  bundleBridge: BundleInstallBridge;
  devtoolsBridge: DevtoolsBridge | undefined;
  autoLaunchBridge: AutoLaunchBridge | undefined;
  systemNotificationSettingsBridge:
    | SystemNotificationSettingsBridge
    | undefined;
} {
  const bundleBridge = useMemo<BundleInstallBridge>(
    () => createBundleBridge(client, { pickLocal }),
    [client, pickLocal],
  );
  const devtoolsBridge = useMemo<DevtoolsBridge | undefined>(
    () =>
      window.hibroDevtools
        ? {
            getState: () => window.hibroDevtools!.getState(),
            setDebug: (input) => window.hibroDevtools!.setDebug(input),
            setDiagnostics: (input) =>
              window.hibroDevtools!.setDiagnostics(input),
            openDevtools: () => window.hibroDevtools!.openDevtools(),
            link: () => window.hibroDevtools!.link(),
            unlink: (dir) => window.hibroDevtools!.unlink(dir),
            reload: () => window.hibroDevtools!.reload(),
          }
        : undefined,
    [],
  );
  const [autoLaunchAvailable, setAutoLaunchAvailable] = useState(false);
  useEffect(() => {
    let active = true;
    void window.hibroAutoLaunch?.available().then((available) => {
      if (active) {
        setAutoLaunchAvailable(available);
      }
    });
    return () => {
      active = false;
    };
  }, []);
  const autoLaunchBridge = useMemo<AutoLaunchBridge | undefined>(
    () =>
      autoLaunchAvailable
        ? {
            isEnabled: () => window.hibroAutoLaunch!.isEnabled(),
            setEnabled: (enabled) =>
              window.hibroAutoLaunch!.setEnabled(enabled),
          }
        : undefined,
    [autoLaunchAvailable],
  );
  const systemNotificationSettingsBridge = useMemo<
    SystemNotificationSettingsBridge | undefined
  >(
    () =>
      window.hibroNotification
        ? { open: () => window.hibroNotification!.openSystemSettings() }
        : undefined,
    [],
  );
  return {
    bundleBridge,
    devtoolsBridge,
    autoLaunchBridge,
    systemNotificationSettingsBridge,
  };
}
